import * as colors from '@giphy/js-brand'
import isMobile from 'is-mobile'
import React, { PureComponent, useState } from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { Button as SharedButton } from 'shared/components/buttons'
import FileAccept from 'shared/components/file-portal/file-accept'
import InputWrapper from 'shared/components/inputs/file-input'
import { STATUS_MESSAGES, STATUS_STYLE } from 'shared/components/message/flash-message'
import { SubheaderSmall } from 'shared/components/text-elements'
import { message } from 'shared/redux/message'
import { IProfilePowerUser } from 'shared/types/user'
import { mobile } from 'shared/util/media-query'
import styled, { css } from 'styled-components'
import { convertToImagingService } from '../../../util/url'
import { useDeleteUserField, useEditUserForm } from '../hooks/edit-field'

const IS_MOBILE = isMobile()
const mimeTypes = ['image/gif', 'image/jpeg', 'image/png']
export const MAX_FILE_SIZE = 2 // MB what you upload is what you get back so need to keep this small-ish
const bannerField = 'banner_image'
const defaultBanner = `/static/img/dashboard/banner.gif`

const fillCss = css`
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
`
type InputProps = {
    onFileAccepted?: () => void
    onPreviewReady: (files: any[]) => void
    onError?: (error: Error) => void
    onRemoveBanner?: () => void
    buttonCopy?: string
    hoverOnly: boolean
    bannerSrc: string
    hasBanner?: boolean
}

const Button = styled(SharedButton)`
    cursor: pointer;
    margin-right: 8px;
    position: relative;
    input[type='file'] {
        ${fillCss}
        opacity: 0;
        cursor: pointer;
    }

    &:last-child {
        margin-right: 0;
    }
`

const BannerSpecs = styled(SubheaderSmall)`
    color: ${colors.giphyWhite};
    opacity: 0.75;
    padding: 3px;
    position: relative;
`

const BannerShadow = styled.div`
    background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
    height: 0;
    left: 0;
    padding-bottom: 100%;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
`

const ButtonContainer = styled.div`
    position: relative;
`

const BannerInputAndCTA = styled.div<{ hoverOnly: boolean }>`
    ${fillCss}
    display: flex;
    align-items: center;
    justify-content: center;
    ${({ hoverOnly }) =>
        hoverOnly &&
        css`
            ${ButtonContainer} {
                opacity: 0;
                transition: opacity linear 0.3s;
            }
            &:hover {
                ${BannerImg} {
                    opacity: 0.5;
                }
                ${ButtonContainer} {
                    opacity: 1;
                }
            }
        `}
`

const BannerImg = styled.img`
    transition: opacity linear 0.3s;
    object-fit: cover;
    ${fillCss}
`
// @ts-ignore
@FileAccept
class BannerInput extends PureComponent<InputProps> {
    render() {
        const { onError, onFileAccepted, buttonCopy, hoverOnly, bannerSrc, onRemoveBanner, hasBanner } = this.props
        const inputProps = {
            name: bannerField,
            onFileAccepted,
            onError,
            mimeTypes,
            maxFilesize: MAX_FILE_SIZE,
        }
        return (
            <BannerInputAndCTA hoverOnly={hoverOnly}>
                <BannerImg src={convertToImagingService(bannerSrc)} />
                <BannerShadow />
                <InputWrapper {...inputProps} mimeTypes={['image/*']} acceptMultiple={false} />
                <ButtonContainer>
                    <Button>
                        <InputWrapper {...inputProps} mimeTypes={['image/*']} acceptMultiple={false} />
                        {buttonCopy}
                    </Button>
                    {hasBanner && (
                        <Button style={{ background: colors.giphyRed }} onClick={onRemoveBanner}>
                            Remove Banner
                        </Button>
                    )}
                    <BannerSpecs>1040 x 160px / GIF, PNG, JPEG</BannerSpecs>
                </ButtonContainer>
            </BannerInputAndCTA>
        )
    }
}

const Form = styled.form<{ isDisabled: boolean }>`
    width: 100%;
    position: relative;
    height: 160px;
    input[type='file'] {
        opacity: 0;
        ${fillCss}
    }
    ${({ isDisabled }) =>
        isDisabled &&
        css`
            opacity: 0.7;
            pointer-events: none;
        `};

    ${mobile.css`
        height: 120px;
    `}
`

type Props = {
    className?: string
    user: IProfilePowerUser
    message: (message: string, type: string) => void
}

const getMessage = (e: Error | string) => {
    const errorMessage = (e as Error).message
    const messageOrKey = errorMessage || e
    const keyMessage = STATUS_MESSAGES[messageOrKey]
    return keyMessage || `Error uploading avatar: ${messageOrKey}}`
}

const Banner = ({ className, user, message }: Props) => {
    const onSuccess = (text: string) => {
        message(text, STATUS_STYLE.SUCCESS)
    }
    const onError = (error: Error | string) => {
        message(getMessage(error), STATUS_STYLE.ERROR)
    }

    const { banner_image } = user
    const [bannerSrc, setBannerSrc] = useState(banner_image || defaultBanner)

    const onDeleteSuccess = (text: string) => {
        onSuccess(text)
        setBannerSrc(defaultBanner)
    }

    const [isSaving, setSavingForm, formRef] = useEditUserForm(user.id, onSuccess, onError)
    const [isDeleting, setDelete] = useDeleteUserField(user.id, bannerField, onDeleteSuccess, onError)

    const userHasABanner = bannerSrc !== defaultBanner
    const buttonCopy = isSaving
        ? 'Saving...'
        : isDeleting
        ? 'Deleting...'
        : userHasABanner
        ? 'Upload New Banner'
        : 'Upload a Banner'

    const onPreviewReady = (files: [any]) => {
        const [file] = files
        const { preview } = file
        setSavingForm(true)
        setBannerSrc(preview)
    }

    const onRemoveBanner = () => {
        // simple confirm for now.
        if (window.confirm(`Are you sure you want to delete your banner?`)) {
            setDelete(true)
        }
    }

    const { user_type, is_community } = user
    const isBannerAllowed = user_type === 'partner' || user_type === 'artist' || is_community

    return isBannerAllowed ? (
        <Form className={className} isDisabled={isSaving || isDeleting} ref={formRef}>
            <BannerInput
                hoverOnly={!IS_MOBILE && userHasABanner && !isDeleting && !isSaving}
                buttonCopy={buttonCopy}
                onPreviewReady={onPreviewReady}
                onError={onError}
                hasBanner={userHasABanner}
                bannerSrc={bannerSrc}
                onRemoveBanner={onRemoveBanner}
            />
        </Form>
    ) : null
}

export default compose(connect(undefined, { message }))(Banner)
